import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { ChildTableColumn } from '../../core/models/table.models';
import { CELL_EDITOR_STYLES } from './cell-editor.styles';

/**
 * Editor host: dispatches to the correct cell editor based on column type.
 * Uses *ngSwitch for simplicity (no dynamic component loading needed).
 */
@Component({
  selector: 'ct-editor-host',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <ng-container [ngSwitch]="column.type">

      <ct-text-editor *ngSwitchCase="'text'"
        [column]="column" [value]="value" [rowId]="rowId" [editHintId]="editHintId"
        [hasError]="hasError" [isRequired]="isRequired" [errorId]="errorId"
        (save)="save.emit($event)" (cancel)="cancel.emit()" (tabSave)="tabSave.emit($event)">
      </ct-text-editor>

      <ct-number-editor *ngSwitchCase="'number'"
        [column]="column" [value]="value" [rowId]="rowId" [editHintId]="editHintId" [step]="'any'"
        [hasError]="hasError" [isRequired]="isRequired" [errorId]="errorId"
        (save)="save.emit($event)" (cancel)="cancel.emit()" (tabSave)="tabSave.emit($event)">
      </ct-number-editor>

      <ct-number-editor *ngSwitchCase="'integer'"
        [column]="column" [value]="value" [rowId]="rowId" [editHintId]="editHintId" [step]="1"
        [hasError]="hasError" [isRequired]="isRequired" [errorId]="errorId"
        (save)="save.emit($event)" (cancel)="cancel.emit()" (tabSave)="tabSave.emit($event)">
      </ct-number-editor>

      <ct-number-editor *ngSwitchCase="'currency'"
        [column]="column" [value]="value" [rowId]="rowId" [editHintId]="editHintId" [step]="'0.01'"
        [hasError]="hasError" [isRequired]="isRequired" [errorId]="errorId"
        (save)="save.emit($event)" (cancel)="cancel.emit()" (tabSave)="tabSave.emit($event)">
      </ct-number-editor>

      <ct-date-editor *ngSwitchCase="'date'"
        [column]="column" [value]="value" [rowId]="rowId" [editHintId]="editHintId"
        [hasError]="hasError" [isRequired]="isRequired" [errorId]="errorId"
        (save)="save.emit($event)" (cancel)="cancel.emit()" (tabSave)="tabSave.emit($event)">
      </ct-date-editor>

      <ct-date-editor *ngSwitchCase="'datetime'"
        [column]="column" [value]="value" [rowId]="rowId" [editHintId]="editHintId"
        [hasError]="hasError" [isRequired]="isRequired" [errorId]="errorId"
        (save)="save.emit($event)" (cancel)="cancel.emit()" (tabSave)="tabSave.emit($event)">
      </ct-date-editor>

      <ct-select-editor *ngSwitchCase="'select'"
        [column]="column" [value]="value" [rowId]="rowId" [editHintId]="editHintId"
        [hasError]="hasError" [isRequired]="isRequired" [errorId]="errorId"
        (save)="save.emit($event)" (cancel)="cancel.emit()" (tabSave)="onTabSave($event)">
      </ct-select-editor>

      <ct-multiselect-editor *ngSwitchCase="'multiselect'"
        [column]="column" [value]="value" [rowId]="rowId" [editHintId]="editHintId"
        [hasError]="hasError" [isRequired]="isRequired" [errorId]="errorId"
        (save)="save.emit($event)" (cancel)="cancel.emit()" (tabSave)="onTabSave($event)">
      </ct-multiselect-editor>

      <ct-checkbox-editor *ngSwitchCase="'checkbox'"
        [column]="column" [value]="value" [rowId]="rowId" [editHintId]="editHintId"
        [hasError]="hasError" [isRequired]="isRequired" [errorId]="errorId"
        (save)="save.emit($event)" (cancel)="cancel.emit()" (tabSave)="tabSave.emit($event)">
      </ct-checkbox-editor>

      <ct-textarea-editor *ngSwitchCase="'textarea'"
        [column]="column" [value]="value" [rowId]="rowId" [editHintId]="editHintId"
        [hasError]="hasError" [isRequired]="isRequired" [errorId]="errorId"
        (save)="save.emit($event)" (cancel)="cancel.emit()" (tabSave)="tabSave.emit($event)">
      </ct-textarea-editor>

      <ct-email-editor *ngSwitchCase="'email'"
        [column]="column" [value]="value" [rowId]="rowId" [editHintId]="editHintId"
        [hasError]="hasError" [isRequired]="isRequired" [errorId]="errorId"
        (save)="save.emit($event)" (cancel)="cancel.emit()" (tabSave)="tabSave.emit($event)">
      </ct-email-editor>

      <ct-phone-editor *ngSwitchCase="'phone'"
        [column]="column" [value]="value" [rowId]="rowId" [editHintId]="editHintId"
        [hasError]="hasError" [isRequired]="isRequired" [errorId]="errorId"
        (save)="save.emit($event)" (cancel)="cancel.emit()" (tabSave)="tabSave.emit($event)">
      </ct-phone-editor>

      <ct-file-editor *ngSwitchCase="'file'"
        [column]="column" [value]="value" [rowId]="rowId" [editHintId]="editHintId"
        [hasError]="hasError" [isRequired]="isRequired" [errorId]="errorId"
        (save)="save.emit($event)" (cancel)="cancel.emit()" (tabSave)="tabSave.emit($event)">
      </ct-file-editor>

      <ct-text-editor *ngSwitchCase="'pii'"
        [column]="column" [value]="value" [rowId]="rowId" [editHintId]="editHintId"
        [hasError]="hasError" [isRequired]="isRequired" [errorId]="errorId"
        (save)="save.emit($event)" (cancel)="cancel.emit()" (tabSave)="tabSave.emit($event)">
      </ct-text-editor>

      <ct-text-editor *ngSwitchCase="'user'"
        [column]="column" [value]="value" [rowId]="rowId" [editHintId]="editHintId"
        [hasError]="hasError" [isRequired]="isRequired" [errorId]="errorId"
        (save)="save.emit($event)" (cancel)="cancel.emit()" (tabSave)="tabSave.emit($event)">
      </ct-text-editor>

      <span *ngSwitchCase="'formula'" class="cell-readonly">{{ value }}</span>

      <ct-text-editor *ngSwitchDefault
        [column]="column" [value]="value" [rowId]="rowId" [editHintId]="editHintId"
        [hasError]="hasError" [isRequired]="isRequired" [errorId]="errorId"
        (save)="save.emit($event)" (cancel)="cancel.emit()" (tabSave)="tabSave.emit($event)">
      </ct-text-editor>

    </ng-container>
  `,
  styles: [CELL_EDITOR_STYLES, `
    .cell-readonly { padding: 8px 16px; color: var(--og-text-secondary, rgba(0,0,0,0.6)); font-style: italic; }
  `],
})
export class EditorHostComponent {
  @Input() column!: ChildTableColumn<any>;
  @Input() value: any;
  @Input() rowId = '';
  @Input() editHintId = '';
  @Input() hasError = false;
  @Input() isRequired = false;
  @Input() errorId = '';

  @Output() save = new EventEmitter<any>();
  @Output() cancel = new EventEmitter<void>();
  @Output() tabSave = new EventEmitter<{ value: any; shiftKey: boolean }>();

  onTabSave(event: { value: any; shiftKey: boolean }): void {
    this.tabSave.emit(event);
  }
}
